<template>
  <div
    ref="wrapper"
    data-test="us-accordion"
    :class="['us-accordion', { 'us-accordion--alt-theme': altTheme }]"
  >
    <div
      v-for="(accordion, i) in accordions"
      :key="i"
      class="us-accordion__item"
    >
      <button class="us-accordion__title display--d2--semibold">
        <div class="us-accordion__title-main">
          <component
            :is="headingTag"
            v-if="accordion.heading"
            class="us-accordion__heading"
          >
            {{ accordion.heading }}
          </component>
          <slot
            v-else
            :name="`heading-slot-${i}`"
            class="us-accordion__heading"
          />
          <span
            v-if="accordion.subHeading"
            class="us-accordion__subheading body--p1--regular"
          >
            {{ accordion.subHeading }}
          </span>
        </div>
        <USIcon
          :diameter="20"
          name="chevron-up"
          class="us-accordion__open-icon"
        />
        <USIcon
          :diameter="20"
          name="chevron-down"
          class="us-accordion__close-icon"
        />
      </button>
      <div class="us-accordion__description">
        <p v-if="accordion.content">
          {{ accordion.content }}
        </p>
        <slot v-else :name="`accordion-${i}`" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  watch
} from 'vue'

import { Accordion } from '@base/@motiv/fe-patterns'

import USIcon from '@base/components/USIcon/USIcon.vue'

interface Action {
  heading: string
  content: string
  /** Shown beside the heading with regular weight when set. */
  subHeading?: string
}

export default defineComponent({
  name: 'USAccordion',
  components: { USIcon },
  props: {
    accordions: { type: Array as PropType<Array<Action>>, required: true },
    exclusive: { type: Boolean, default: true },
    headingLevel: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
      default: 3
    },
    openIndex: {
      type: Number,
      default: -1
    },
    /** Dense title bar (primary fill, light text) and adjusted panel spacing. */
    altTheme: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:openIndex'],
  setup(props, { emit }) {
    const wrapper = ref<HTMLElement>()
    const headingTag = computed(() => `h${props.headingLevel}`)
    let accordion: Accordion | null = null
    let descriptionResizeObserver: ResizeObserver | null = null
    let descriptionMutationObserver: MutationObserver | null = null
    let observedDescEl: HTMLElement | null = null
    let observedImageLoadListeners: Array<{
      img: HTMLImageElement
      onLoad: () => void
      onError: () => void
    }> = []

    const emitListeners: Array<{ el: Element; fn: () => void }> = []

    function disconnectDescriptionResizeObserver() {
      descriptionResizeObserver?.disconnect()
      descriptionResizeObserver = null
    }

    function disconnectDescriptionMutationObserver() {
      descriptionMutationObserver?.disconnect()
      descriptionMutationObserver = null
      for (const { img, onLoad, onError } of observedImageLoadListeners) {
        img.removeEventListener('load', onLoad)
        img.removeEventListener('error', onError)
      }
      observedImageLoadListeners = []
      observedDescEl = null
    }

    function remeasureOpenDescription(panelIndex: number) {
      if (!wrapper.value) return
      const items = wrapper.value.querySelectorAll('.us-accordion__item')
      const item = items[panelIndex]
      const desc = item?.querySelector(
        '.us-accordion__description'
      ) as HTMLElement | null
      if (!item?.classList.contains('open') || !desc) return
      desc.style.maxHeight = `${desc.scrollHeight}px`
    }

    /**
     * Motiv sets max-height from scrollHeight immediately; slot content often
     * lays out a frame later (initial load / nested components). Defer remeasure
     * and observe description size so max-height tracks the true content height.
     */
    function scheduleRemeasureAfterOpen(panelIndex: number) {
      disconnectDescriptionResizeObserver()
      disconnectDescriptionMutationObserver()

      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            remeasureOpenDescription(panelIndex)
            requestAnimationFrame(() => {
              remeasureOpenDescription(panelIndex)
              requestAnimationFrame(() => {
                remeasureOpenDescription(panelIndex)
                attachDescriptionResizeObserver(panelIndex)
                attachDynamicContentObservers(panelIndex)
              })
            })
          })
        })
      })
    }

    function attachDescriptionResizeObserver(panelIndex: number) {
      if (!wrapper.value) return
      const items = wrapper.value.querySelectorAll('.us-accordion__item')
      const desc = items[panelIndex]?.querySelector(
        '.us-accordion__description'
      ) as HTMLElement | null
      if (!desc) return

      descriptionResizeObserver = new ResizeObserver(() => {
        remeasureOpenDescription(panelIndex)
      })
      descriptionResizeObserver.observe(desc)
    }

    /**
     * Dynamic slot content (tables, tags, image thumbnails) may update after the
     * panel opens. Observe subtree mutations and image load/error to keep the
     * open panel max-height in sync.
     */
    function attachDynamicContentObservers(panelIndex: number) {
      if (!wrapper.value) return
      const items = wrapper.value.querySelectorAll('.us-accordion__item')
      const desc = items[panelIndex]?.querySelector(
        '.us-accordion__description'
      ) as HTMLElement | null
      if (!desc) return

      observedDescEl = desc
      const remeasure = () => remeasureOpenDescription(panelIndex)

      descriptionMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(remeasure)
        requestAnimationFrame(() => {
          attachImageLoadListeners(panelIndex)
        })
      })
      descriptionMutationObserver.observe(desc, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      })

      attachImageLoadListeners(panelIndex)
    }

    function attachImageLoadListeners(panelIndex: number) {
      if (!observedDescEl) return
      const remeasure = () => remeasureOpenDescription(panelIndex)
      const images = Array.from(observedDescEl.querySelectorAll('img'))

      for (const img of images) {
        if (observedImageLoadListeners.some((entry) => entry.img === img)) {
          continue
        }
        const onLoad = () => requestAnimationFrame(remeasure)
        const onError = () => requestAnimationFrame(remeasure)
        img.addEventListener('load', onLoad)
        img.addEventListener('error', onError)
        observedImageLoadListeners.push({ img, onLoad, onError })
      }
    }

    function clearDescriptionHeights() {
      if (!wrapper.value) return
      wrapper.value
        .querySelectorAll('.us-accordion__description')
        .forEach((el) => {
          ;(el as HTMLElement).style.removeProperty('max-height')
        })
    }

    function syncOpenFromProp() {
      if (!accordion) return
      const idx = props.openIndex
      const n = props.accordions.length
      if (n === 0) return

      if (idx < 0) {
        disconnectDescriptionResizeObserver()
        disconnectDescriptionMutationObserver()
        for (let i = 0; i < n; i++) {
          accordion.close(i)
        }
        return
      }

      const clamped = Math.min(Math.max(0, idx), n - 1)
      disconnectDescriptionResizeObserver()
      disconnectDescriptionMutationObserver()
      clearDescriptionHeights()
      accordion.open(clamped)
      scheduleRemeasureAfterOpen(clamped)
    }

    function attachHeaderEmitSync() {
      if (!wrapper.value) return
      const items = wrapper.value.querySelectorAll('.us-accordion__item')
      items.forEach((item) => {
        const title = item.querySelector('.us-accordion__title')
        if (!title) return
        const fn = () => {
          requestAnimationFrame(() => {
            if (!wrapper.value) return
            const all = wrapper.value.querySelectorAll('.us-accordion__item')
            const openIdx = Array.from(all).findIndex((el) =>
              el.classList.contains('open')
            )
            emit('update:openIndex', openIdx)
          })
        }
        title.addEventListener('click', fn)
        emitListeners.push({ el: title, fn })
      })
    }

    function detachHeaderEmitSync() {
      for (const { el, fn } of emitListeners) {
        el.removeEventListener('click', fn)
      }
      emitListeners.length = 0
    }

    function initAccordionInstance() {
      disconnectDescriptionResizeObserver()
      disconnectDescriptionMutationObserver()
      accordion?.destory()
      accordion = null
      detachHeaderEmitSync()

      if (!wrapper.value) return

      accordion = new Accordion(
        wrapper.value,
        '.us-accordion__item',
        '.us-accordion__title',
        '.us-accordion__description',
        props.exclusive
      )

      attachHeaderEmitSync()

      nextTick(() => {
        syncOpenFromProp()
      })
    }

    onMounted(() => {
      initAccordionInstance()
    })

    watch(
      () => props.accordions.length,
      () => {
        nextTick(() => initAccordionInstance())
      }
    )

    watch(
      () => props.openIndex,
      () => {
        nextTick(() => syncOpenFromProp())
      },
      { flush: 'post' }
    )

    onBeforeUnmount(() => {
      disconnectDescriptionResizeObserver()
      disconnectDescriptionMutationObserver()
      detachHeaderEmitSync()
      accordion?.destory()
      accordion = null
    })

    return {
      headingTag,
      wrapper
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

@use '@base/@motiv/fe-patterns/styles/accordion';

@include accordion.accordion(
  $accordion-selector: '.us-accordion',
  $item-selector: '.us-accordion__item',
  $title-selector: '.us-accordion__title',
  $description-selector: '.us-accordion__description',
  $close-icon-selector: '.us-accordion__close-icon',
  $open-icon-selector: '.us-accordion__open-icon',
  $icon-size: --iconSize,
  $icon-top-offset: 0.1em,
  $transition-speed: var(--animation-speed-medium)
);

.us-accordion {
  --iconSize: 20px;

  color: var(--color--neutral-a1);

  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    --iconSize: 24px;
  }

  &__item {
    border-bottom: var(--line-weight--heavy) solid var(--color--primary-b1);
    padding-top: var(--spacing--5);

    &.open {
      .us-accordion__description {
        margin-bottom: var(--spacing--6);
      }
    }
  }

  &__title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--spacing--4);
    padding-bottom: var(--spacing--7);
    padding-right: var(--spacing--6);
    text-align: left;
    width: 100%;
  }

  &__title-main {
    display: flex;
    flex: 1 1 auto;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--spacing--9);
    min-width: 0;
    max-width: 90%;
  }

  &__heading {
    flex: 0 0 auto;
    margin: 0;
  }

  &__subheading {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__description {
    font-family: var(--font-family--poppins);
    font-size: var(--font-size--z7);
    font-weight: var(--font-weight--regular);
    letter-spacing: var(--letter-spacing--ls0);
    line-height: var(--line-height--default);
  }
}

.us-accordion.us-accordion--alt-theme {
  .us-accordion__item {
    padding-top: var(--spacing--9);
    border-bottom: none;

    &.open .us-accordion__description {
      margin-bottom: 0;
    }
  }

  .us-accordion__title {
    align-items: center;
    padding: var(--spacing--7);
    background-color: var(--color--primary-b1);
    color: var(--color--neutral-a5);

    /* Chevron toggles only — not icons inside panel slots (e.g. step actions). */
    :deep(.us-icon) {
      color: var(--color--neutral-a5);
    }
  }

  .us-accordion__heading,
  .us-accordion__subheading {
    color: var(--color--neutral-a5);
  }
}

:deep(.us-icon) {
  color: var(--color--primary-b1);
}
</style>
